<template lang="pug">
student-list.student-management(
  ref='template',
  fullNamePlaceholder='氏名',
  academyNamePlaceholder='塾名',
  branchNamePlaceholder='教室名',
  statusPlaceholder='ステータス',
  modalTitle='ユーザー登録（生徒）',
  modalUserNamePlaceholder='氏名',
  modalUserBcbtCodePlaceholder='ID',
  modalPasswordPlaceholder='パスワード',
  modalPasswordConfirmPlaceholder='パスワード',
  modalStatusPlaceholder='ステータス',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :setRegisterDataEvent='setRegisterDataEvent',
  :tabHeadingEvent='tabHeadingEvent',
  :gradeSelectOptions='gradeSelectOptions',
  :searchEvent='searchEvent',
  :qrOutputEvent='qrOutputEvent',
  :downloadEvent='downloadEvent',
  :uploadEvent='uploadEvent',
  :deleteEvent='deleteEvent',
  :sort='sort',
  :studentDatas='studentDatas',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :studentEditData='studentEditData',
  :modalAcademySelectOptions='modalAcademySelectOptions',
  :modalAcademyChangeEvent='modalAcademyChangeEvent',
  :modalBranchSelectOptions='modalBranchSelectOptions',
  :modalGradeSelectOptions='modalGradeSelectOptions',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#breadcrumbButtonLabel) 新規作成
  template(#searchButtonText) 絞り込む
  template(#qrOutputIconLabel) QRコード
  template(#downloadIconLabel) ダウンロード
  template(#uploadIconLabel) アップロード
  template(#userNameHeader) 氏名
  template(#bcbtCodeHeader) ID
  template(#academyNameHeader) 塾
  template(#branchNameHeader) 教室
  template(#gradeNameHeader) 学年
  template(#statusNameHeader) ステータス
  template(#editHeader) 編集
  template(#popupRemoveText) 削除
  template(#popupEditText) 編集
  template(#modalAcademyNameLabel) 塾名
  template(#modalAcademyNameDisabledLabel) 塾名（編集不可）
  template(#modalBranchNameLabel) 教室名
  template(#modalBranchNameDisabledLabel) 教室名（編集不可）
  template(#modalGradeNameLabel) 学年
  template(#modalUserNameLabel) 氏名
  template(#modalUserBcbtCodeLabel) ID
  template(#modalPasswordLabel) パスワード
  template(#modalPasswordLabelAnnotation) ※変更がない場合は空白
  template(#modalPasswordConfirmLabel) パスワード
  template(#modalPasswordConfirmLabelAnnotation) ※変更する場合の再確認用
  template(#modalStatusLabel) ステータス
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import GradesApi from '@/mixins/api/GradesApi'
import StudentsApi from '@/mixins/api/StudentsApi'
import AcademiesApi from '@/mixins/api/AcademiesApi'
import BranchesApi from '@/mixins/api/BranchesApi'
import StudentList from '@/components/templates/manager/StudentList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  StudentListType,
  StudentListSortType,
  StudentEditType,
  StudentSearchParamsType,
} from '@/models/studentListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting, InputPatternSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { ManagerInputUtils } from '@/utils/ManagerInputUtils'
import { StudentManagementUtils } from '@/utils/StudentManagementUtils'

/**
 * システム管理者・生徒管理画面
 */
@Component({
  components: {
    StudentList,
  },
})
export default class StudentManagement extends Mixins(LoginMethods, GradesApi, StudentsApi, AcademiesApi, BranchesApi) {
  // テンプレートへのアクセス
  @Ref() template!: StudentList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'ユーザー管理',
    },
  ]

  // システム管理者権限
  private userRole: UserRoles = UserRoles.ADMIN

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_USER

  // ソート情報
  private sort: StudentListSortType | null = null

  // 検索用の学年選択オプション
  private gradeSelectOptions: InputItem[] = []

  // 生徒一覧情報
  private studentDatas: StudentListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示する生徒編集情報
  private studentEditData: StudentEditType | null = null

  // モーダルの塾選択オプション
  private modalAcademySelectOptions: InputItem[] = []

  // モーダルの教室選択オプション
  private modalBranchSelectOptions: InputItem[] = []

  // モーダルの学年選択オプション
  private modalGradeSelectOptions: InputItem[] = []

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在の検索パラメーター
  private currentSearchParams: StudentSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    // 学年選択オプションAPI呼び出し
    const gradeApiResponse = await this.getGrades()
    // 塾選択オプションAPI呼び出し
    const AcademyApiResponse = await this.getAcademies()
    // 検索用の学年選択オプションを設定
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE_ALL),
      gradeApiResponse
    )
    // モーダル用の学年選択オプションを設定
    this.modalGradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE),
      gradeApiResponse
    )
    // モーダル用の塾プルダウンのオプションを取得
    this.modalAcademySelectOptions = ManagerInputUtils.buildAcademyInputItems(
      ManagerInputUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_ACADEMY),
      AcademyApiResponse
    )
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      userName: SortSettings.ASC,
      bcbtCode: SortSettings.ASC,
      academyName: SortSettings.ASC,
      branchName: SortSettings.ASC,
      gradeName: SortSettings.ASC,
      statusName: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // 全チェックボックス、ソートを初期化
    this.template.checkedAll = false
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: StudentListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 生徒一覧検索API呼び出し
    this.currentSearchParams = {
      page: page,
      gradeId: this.template.gradeValue ? Number(this.template.gradeValue) : null,
      userName: this.template.fullName,
      academyName: this.template.academyName,
      branchName: this.template.branchName,
      statusName: this.template.status,
      userNameSort: this.sort.userName.code,
      bcbtCodeSort: this.sort.bcbtCode.code,
      academyNameSort: this.sort.academyName?.code || null,
      branchNameSort: this.sort.branchName?.code || null,
      gradeSort: this.sort.gradeName.code,
      statusNameSort: this.sort.statusName.code,
    }
    const apiResponse = await this.searchStudents(this.currentSearchParams, this.userRole)
    this.studentDatas = StudentManagementUtils.formatStudentDatas(apiResponse)
    this.pagenationStatus = StudentManagementUtils.getPagenationStatus(apiResponse)
    // チェックボックスのチェック状態をセット
    this.setCheckedTeacherUserIds()
  }

  // チェックボックスのチェック状態をセットする処理
  private setCheckedTeacherUserIds() {
    if (this.template.checkedAll) {
      // 全チェックボックスがチェックされている場合、全データのチェックボックスをチェックする
      this.template.checkedStudentUserIds = this.studentDatas.map((studentData) => {
        return studentData.id
      })
    } else {
      // チェックされていない場合は全てを未チェックにする
      this.template.checkedStudentUserIds = []
    }
  }

  // QRコード出力イベント
  private async qrOutputEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedStudentUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // 出力処理
    this.$loading.start()
    // チェックボックスがチェックされた生徒情報を絞り込みに使用する（全選択時は絞り込み不要）
    const studentIds: number[] = this.template.checkedAll ? [] : this.template.checkedStudentUserIds
    try {
      // 直近の検索パラメーターを用いてpdfデータを取得する
      const exportData = await this.downloadStudentQrLoginPdf(this.currentSearchParams, studentIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = `student_qr_${this.$localDateFns.formatTimestamp(new Date())}.pdf`
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedStudentUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // ダウンロード処理
    this.$loading.start()
    // チェックボックスがチェックされた生徒情報を絞り込みに使用する（全選択時は絞り込み不要）
    const studentIds: number[] = this.template.checkedAll ? [] : this.template.checkedStudentUserIds
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportStudentExcel(this.currentSearchParams, studentIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '生徒ユーザ一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // アップロードイベント
  private async uploadEvent(file: File) {
    this.$loading.start()
    try {
      await this.importStudentExcel(file, this.userRole)
      alert('取込みが完了しました')
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      alert(e.response.data.message.message)
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Admin${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AdminLogin' })
  }

  // 見出しタブ押下イベント
  private tabHeadingEvent(name: string) {
    // リンク先ページへ遷移
    this.$router.push({ name: `Admin${name}` })
  }

  // モーダルの塾プルダウン変更時の挙動
  private async modalAcademyChangeEvent() {
    if (!this.studentEditData) return

    this.$loading.start()
    // 教室プルダウンのオプションをセットする
    await this.setBranchSelectOptions(this.studentEditData.academyId)
    // 教室名プルダウンを未選択状態に戻す
    if (this.studentEditData.branchId) this.studentEditData.branchId = ''
    this.$loading.complete()
  }

  // 教室名プルダウンのオプション取得処理
  private async setBranchSelectOptions(academyId?: string) {
    // 教室選択オプションAPI呼び出し
    const apiResponse = academyId ? await this.getBranches(this.userRole, Number(academyId)) : null
    this.modalBranchSelectOptions = ManagerInputUtils.buildBranchInputItems(
      ManagerInputUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_BRANCH),
      apiResponse
    )
  }

  private setRegisterDataEvent() {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    // 初期値を設定
    this.studentEditData = StudentManagementUtils.buildAdminStudentEditData()
    // 教室プルダウンオプションの初期値を設定
    this.setBranchSelectOptions()
  }

  private async setEditDataEvent(id: number) {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    // 生徒情報取得API呼び出し
    const apiResponse = await this.getStudentData(id, this.userRole)
    this.studentEditData = StudentManagementUtils.buildAdminStudentEditData(apiResponse)
    // 教室プルダウンのオプションをセットする
    await this.setBranchSelectOptions(this.studentEditData.academyId)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent(): Promise<void> {
    if (!this.studentEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    // 生徒ユーザの登録・修正の際にIDのパターンが正しくない場合はエラーを返して登録・修正処理を行わない
    if (!this.studentEditData.bcbtCode.match(InputPatternSetting.ACCOUNT_PATTERN)) {
      this.modalErrorMessages = [InputPatternSetting.ACCOUNT_PATTERN_ERROR_MESSAGE]
      return
    }
    this.$loading.start()
    try {
      if (this.studentEditData.id) {
        // studentEditDataにIDがある場合は更新処理
        await this.updateStudentData(this.studentEditData, this.userRole)
      } else {
        // ない場合は新規登録
        await this.createStudentData(this.studentEditData, this.userRole)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }

  // 削除イベント
  private async deleteEvent(id: number) {
    this.$loading.start()
    try {
      await this.deleteStudentData(id, this.userRole)
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー時はエラーメッセージをアラート
      alert(e.response.data.message)
    }
  }
}
</script>
